import { CertificateViewAccess } from '@repo/types'
import type { CertificateView } from 'hooks/api/useCertificateViews'

/**
 * The two facts a share row has to answer, as its subtitle.
 *
 * They are separate functions rather than one sentence because they answer
 * different questions — WHO can open this, and WHAT will they see — and a row
 * can want one without the other.
 */

/**
 * Who can reach this link.
 *
 * Replaces the raw URL, which was truncated to unreadability at mobile width
 * (`…/api/s/d0ff…`) and un-actionable anyway, since Share already puts it on the
 * clipboard (desktop fallback) or hands it off directly. Access state is the fact
 * the owner is actually scanning this list for.
 *
 * A bare viewer COUNT would be wrong: on an `anyone` view the number is irrelevant
 * (everyone holding the link is in), so rendering "0" there would read as "nobody has
 * access" when the opposite is true. Hence the access mode gates the wording.
 *
 * "No one yet" also surfaces the empty-allowlist state — an invited-only link with an
 * empty list opens for nobody — in the list rather than a tap deep in Settings.
 */
export const accessSummary = (view: CertificateView): string => {
  if (view.access !== CertificateViewAccess.Invited) return 'Anyone with the link'

  const count = view.viewer_count ?? 0
  if (count === 0) return 'No one yet'
  return count === 1 ? '1 person' : `${count} people`
}

/**
 * What this view withholds — the privacy disclosure, per view.
 *
 * ## Why it lives on the ROW rather than under the list
 *
 * This started life as a grey info card on the certificate page, moved to a
 * paragraph at the top of Share, then to a paragraph directly beneath the public
 * row. Each move fixed the same complaint in a smaller way: the sentence says
 * "hidden from THIS view" and was never attached to a view.
 *
 * A paragraph under one row cannot be attached to a view, because there is more
 * than one row. It was hardcoded to the public view and every custom link — which
 * carries its own independent visibility toggles — silently had no disclosure at
 * all. That is the actual bug, and it is structural: the only place a per-view
 * fact belongs is inside the per-view row.
 *
 * ## Why it names the HIDDEN modules
 *
 * Hidden is the surprising state and the one worth acting on. Listing what is
 * SHOWN would be longer on the common case (most views show most things) and
 * would bury the one module the owner switched off.
 *
 * The fully-open case still renders a sentence rather than nothing: an owner
 * scanning this list needs "this link exposes everything" to be a statement the
 * screen makes, not an absence they have to infer. It also keeps every row the
 * same height, which is what makes the list scannable.
 */
export const visibilitySummary = (view: CertificateView): string => {
  if (!view.visibility) return 'Shows everything'

  const hidden = [
    !view.visibility.show_ownership && 'ownership',
    !view.visibility.show_provenance && 'history',
    !view.visibility.show_prices && 'prices',
    !view.visibility.show_activity && 'scans',
  ].filter(Boolean) as string[]

  if (hidden.length === 0) return 'Shows everything'

  /*
   * Short module names, not the prose ones the old paragraph used ("your
   * ownership", "ownership history", "sale prices"). A subtitle shares a line
   * with the access summary and sits under a 16px title, so it has room for a
   * list of nouns and not for a sentence. The words are unambiguous in the
   * context of a certificate, which is the only place this renders.
   */
  return `${hidden.join(', ')} hidden`
}
